import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../../services/otpAuthService';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

interface MemberResult {
  _id: string;
  businessName: string;
  city?: string;
  province?: string;
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string) => void;
  onMentionsChange: (ids: string[]) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  onPasteFiles?: (files: File[]) => void;
}

export default function MentionTextarea({
  value,
  onChange,
  onMentionsChange,
  placeholder,
  rows = 3,
  className = '',
  disabled = false,
  onPasteFiles
}: MentionTextareaProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<MemberResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchTimeout = useRef<NodeJS.Timeout>(undefined);

  const searchMembers = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); return; }
    try {
      const res = await axios.get(`${API_BASE_URL}/api/community/v2/members/search`, {
        params: { q },
        headers: getAuthHeader() as any
      });
      if (res.data.success) setResults(res.data.data || []);
    } catch { setResults([]); }
  }, []);

  useEffect(() => {
    if (!showDropdown || !searchQuery) return;
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchMembers(searchQuery), 250);
    return () => clearTimeout(searchTimeout.current);
  }, [searchQuery, showDropdown, searchMembers]);

  const insertMention = (member: MemberResult) => {
    const before = value.slice(0, mentionStart);
    const after = value.slice(textareaRef.current?.selectionStart || mentionStart);
    const mentionText = `@${member.businessName} `;
    const newValue = before + mentionText + after;
    onChange(newValue);

    const newIds = [...mentionIds, member._id];
    setMentionIds(newIds);
    onMentionsChange(newIds);

    setShowDropdown(false);
    setSearchQuery('');
    setResults([]);

    setTimeout(() => {
      const pos = before.length + mentionText.length;
      textareaRef.current?.setSelectionRange(pos, pos);
      textareaRef.current?.focus();
    }, 0);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    const cursorPos = e.target.selectionStart;
    const textBefore = newValue.slice(0, cursorPos);
    const atIndex = textBefore.lastIndexOf('@');

    if (atIndex >= 0) {
      const charBefore = atIndex > 0 ? textBefore[atIndex - 1] : ' ';
      if (charBefore === ' ' || charBefore === '\n' || atIndex === 0) {
        const query = textBefore.slice(atIndex + 1);
        if (!query.includes(' ') && !query.includes('\n')) {
          setMentionStart(atIndex);
          setSearchQuery(query);
          setShowDropdown(true);
          setSelectedIndex(0);
          return;
        }
      }
    }
    setShowDropdown(false);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (!onPasteFiles) return;
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      e.preventDefault();
      onPasteFiles(files);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className={className}
      />
      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto"
        >
          {results.map((member, i) => (
            <button
              key={member._id}
              type="button"
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                i === selectedIndex
                  ? 'bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              }`}
              onMouseDown={(e) => { e.preventDefault(); insertMention(member); }}
              onMouseEnter={() => setSelectedIndex(i)}
            >
              <span className="font-medium">{member.businessName}</span>
              {member.city && (
                <span className="text-xs text-gray-400">
                  {member.city}{member.province ? ` (${member.province})` : ''}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
